import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { formatToReal } from "../../../utils/utils";

const Protestos = ({
  protestos = [],
  quantidadeTotal,
  primeiroValor,
  valorTotal,
  temEstado = true,
  temVencimento = true,
  isNacional = false,
}) => {
  const [expandido, setExpandido] = useState(false);

  const LIMITE = 10;
  const total = protestos.length;
  const restantes = Math.max(total - LIMITE, 0);

  const listaExibida = useMemo(
    () => (expandido ? protestos : protestos.slice(0, LIMITE)),
    [expandido, protestos]
  );

  const exibirAnuencia =
    Array.isArray(protestos) &&
    protestos.some((p) => p?.temAnuencia !== undefined);

  let gridClass = 5;

  if (exibirAnuencia) gridClass += 1;
  if (temEstado) gridClass += 1;
  if (temVencimento) gridClass += 1;

  return (
    <>
      {/* Resumo */}
      <div className="flex flex-wrap gap-3 mb-5">
        {quantidadeTotal && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium">
            <span>Quantidade Total:</span>
            <span>{quantidadeTotal}</span>
          </div>
        )}

        {primeiroValor && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium">
            <span>Primeiro valor:</span>
            <span>{formatToReal(primeiroValor)}</span>
          </div>
        )}

        {valorTotal && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium">
            <span>Valor total:</span>
            <span>{formatToReal(valorTotal)}</span>
          </div>
        )}
      </div>

      {/* Tabela */}
      <div className="relative rounded-2xl border border-gray-200 overflow-hidden">
        <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
          <div
            className={`grid gap-4 text-xs font-semibold text-gray-600 uppercase tracking-wider grid-cols-${gridClass}`}
          >
            <div>Data</div>
            <div>Valor</div>
            <div className="col-span-2">Cartório</div>
            <div>Cidade</div>
            <div>Estado</div>
            {temVencimento && <div>Vencimento</div>}
            {exibirAnuencia && <div>Anuência</div>}
          </div>
        </div>

        {/* Linhas */}
        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          {listaExibida.map((protesto, index) => (
            <div
              key={index}
              className={`grid gap-4 px-4 py-3 text-sm items-center grid-cols-${gridClass} ${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              }`}
            >
              <div className="text-gray-700">
                {protesto?.data || "Não Divulgado"}
              </div>
              <div className="font-semibold text-red-700">
                {formatToReal(protesto?.valor)}
              </div>
              <div className="font-medium text-gray-900 col-span-2">
                {protesto?.cartorio != null &&
                String(protesto.cartorio).trim() !== ""
                  ? typeof protesto.cartorio === "string" &&
                    /^\d+$/.test(protesto.cartorio.trim())
                    ? `${parseInt(protesto.cartorio.trim(), 10)}º cartório`
                    : protesto.cartorio
                  : "-"}
              </div>
              <div className="text-gray-700">{protesto?.cidade || "-"}</div>
              <div className="text-gray-700">{protesto?.estado || "-"}</div>
              {temVencimento && (
                <div className="text-gray-700">
                  {protesto?.vencimento || "-"}
                </div>
              )}
              {exibirAnuencia && (
                <div className="text-gray-700">
                  {protesto?.temAnuencia ? "Sim" : "Não"}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-t border-gray-200">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-gray-500">(+{restantes} restantes)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {!expandido && total > LIMITE && (
        <div className="text-sm mt-3 text-gray-500 text-center">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
      {isNacional && (
        <div className="mt-3 border border-gray-300 bg-gray-50 rounded-md p-2 text-sm">
          Atenção: Em alguns casos, o Protesto Nacional não exibe a data do
          protesto porque o cartório de origem não disponibilizou essa
          informação.
        </div>
      )}
    </>
  );
};

export default Protestos;
